import "./SeedNavigation.css";

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { SeedLogo } from "../../../assets";
import { ColorConstants, OrganisationTypeConstants } from "../../../constants";
import { SeedActiveElement } from "../../../models";
import { getDashboardRoute } from "../../../utils/routes";
import { Avatar } from "../../data";
import { Button } from "../../forms";
import { Divider, HelpCenter } from "../../general";
import { NavigationTooltip } from "../LeftNavigation/components";
import { ProfileMenu } from "../ProfileMenu";
import { ProfileMenuItem, ProfileMenuItemType } from "../ProfileMenuItem";
import { HeaderTextElement, useSeedNavigation } from "./useSeedNavigation";

interface SeedNavigationProps {
  activeElement?: SeedActiveElement;
}

export const SeedNavigation = ({ activeElement }: SeedNavigationProps): JSX.Element => {
  const navigate = useNavigate();
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const {
    showProfileMenu,
    setShowProfileMenu,
    showHelpCenter,
    setShowHelpCenter,
    currentUserData,
    optionList,
    onOptionSelect,
    headerIcons,
    headerTextElements,
    logoUrl,
  } = useSeedNavigation();

  const onHeaderDropdownSelect = (item: ProfileMenuItemType): void => {
    if (item.action) {
      item.action();
    }
    if (item.route) {
      navigate(item.route);
    }
    setActiveDropdown(null);
  };

  const isDropdownElement = (element: HeaderTextElement): element is { label: string; links: ProfileMenuItemType[] } =>
    "links" in element;

  const renderHeaderIcon = (element: (typeof headerIcons)[number]): JSX.Element => {
    if (element.route) {
      return (
        <Link
          to={element.route}
          className={`body2 NavigationElement ${activeElement === element.key && "NavigationElement_active"}`}
        >
          {element.icon}
        </Link>
      );
    }
    if (element.onClick) {
      return (
        <Button
          className={`NavigationElement ${showHelpCenter && "NavigationElement_active"}`}
          onClick={element.onClick}
          text=""
          icon={element.icon}
          hasNoPadding
          minWidth={false}
          variant="quinary"
          size="small"
        />
      );
    }
    return element.icon;
  };

  return (
    <nav className="SeedNavigation">
      <div className="HeaderContainer">
        <div className="HeaderLeftContent">
          <Link to={getDashboardRoute(OrganisationTypeConstants.ASSET_MANAGER)}>
            {logoUrl ? (
              <img className="NavigationLogoImage" src={logoUrl} alt="Organisation logo" />
            ) : (
              <SeedLogo className="NavigationLogo" />
            )}
          </Link>
        </div>
        <div className="HeaderRightContent">
          <div className="TextElements">
            {headerTextElements.map((element) =>
              isDropdownElement(element) ? (
                <div key={element.label} className="NavigationDropdown">
                  <button
                    type="button"
                    className={`body2 NavigationElement ${activeElement === element.label && "NavigationElement_active"}`}
                    onClick={() => setActiveDropdown(activeDropdown === element.label ? null : element.label)}
                  >
                    {element.label}
                  </button>
                  <ProfileMenu
                    show={activeDropdown === element.label}
                    onClose={() => setActiveDropdown(null)}
                    position={{ left: "0" }}
                    closeOnScroll
                    corners="rounded"
                  >
                    {element.links.map((link) => (
                      <div key={link.id ?? link.value} className="ProfileMenuItemWrapper">
                        <ProfileMenuItem profileMenuItem={link} onClick={() => onHeaderDropdownSelect(link)} />
                      </div>
                    ))}
                  </ProfileMenu>
                </div>
              ) : (
                <Link
                  key={element.label}
                  to={element.route}
                  className={`body2 NavigationElement ${activeElement === element.label && "NavigationElement_active"}`}
                >
                  {element.label}
                </Link>
              )
            )}
          </div>
          <div className="Icons">
            {headerIcons.map((element) => (
              <NavigationTooltip key={element.key} text={element.key} corners="rounded">
                {renderHeaderIcon(element)}
              </NavigationTooltip>
            ))}
          </div>
          <div className="User">
            <Avatar
              imageSource={currentUserData?.avatar?.url}
              name={`${currentUserData?.firstName} ${currentUserData?.lastName}`}
              withName
              onClick={() => setShowProfileMenu(true)}
              textColor={ColorConstants.INDIGO_400}
            />
            <ProfileMenu show={showProfileMenu} onClose={() => setShowProfileMenu(false)} corners="rounded">
              {optionList.map((option, index) => (
                <div key={option.id} className="ProfileMenuItemWrapper">
                  <ProfileMenuItem profileMenuItem={option} onClick={() => onOptionSelect(option)} />
                  {(index === 1 || index === 2) && <Divider className="ProfileMenuItemDivider" type="horizontal" />}
                </div>
              ))}
            </ProfileMenu>
          </div>
        </div>
      </div>
      <HelpCenter show={showHelpCenter} onClose={() => setShowHelpCenter(false)} />
    </nav>
  );
};
